import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useAuth } from '@/context/AuthContext';
import Layout from '@components/Layout';
import { toast } from 'react-hot-toast';
import { db } from '@root/firebase'; // Adjust the import path based on your setup
import { collection, onSnapshot, addDoc } from 'firebase/firestore';

import Tour from '@components/Tour';
//import { libraryAdminTourSteps } from "@utils/TourSteps"

import { myCalendarColors } from '@utils/Index';

function Calendar() {
  const { globalUser, logout } = useAuth();

  const [events, setEvents] = useState([
    { title: 'Event 1', start: '2024-01-15', end: '2024-01-16' },
    {
      title: 'Event 2',
      start: '2024-01-20T10:00:00',
      end: '2024-01-20T12:00:00',
    },
  ]);

  const [eventName, setEventName] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventCategory, setEventCategory] = useState('');
  const [eventReoccurring, setEventReoccurring] = useState('');
  const [eventStartDate, setEventStartDate] = useState('');
  const [eventStartTime, setEventStartTime] = useState('');
  const [eventEndDate, setEventEndDate] = useState('');
  const [eventEndTime, setEventEndTime] = useState('');
  const [isLoading, setIsLoading] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('');

  const handleDateClick = (arg) => {
    setEventStartDate(arg.dateStr); // Set the selected date
    setEventEndDate(arg.dateStr); // Set the selected date
    const modal = new bootstrap.Modal(document.getElementById('eventModal')); // Open the modal
    modal.show();
  };

  useEffect(() => {
    if (!globalUser?.uid) return;

    const eventsCollectionRef = collection(
      db,
      `users/${globalUser.uid}/events`
    );

    const unsubscribe = onSnapshot(eventsCollectionRef, (snapshot) => {
      const fetchedEvents = snapshot.docs.map((doc) => ({
        id: doc.id, // Include the Firestore document ID
        ...doc.data(),
      }));
      setEvents(fetchedEvents);
    });

    return () => unsubscribe(); // Cleanup on unmount
  }, [globalUser?.uid]);

  const getEventColor = (category) => {
    const colorItem = myCalendarColors.find((item) => item.value === category);
    return colorItem ? `bg-${colorItem.color}` : 'bg-secondary'; // Default color if no match
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();

    // Centralized validation
    const requiredFields = [
      { value: eventName, label: 'Event Name' },
      { value: eventLocation, label: 'Event Location' },
      { value: eventDescription, label: 'Event Description' },
      { value: eventCategory, label: 'Event Category' },
      { value: eventStartDate, label: 'Start Date' },
      { value: eventStartTime, label: 'Start Time' },
      { value: eventEndDate, label: 'End Date' },
      { value: eventEndTime, label: 'End Time' },
    ];

    const missingFields = requiredFields.filter((field) => !field.value.trim());
    if (missingFields.length > 0) {
      toast.error(
        `Please fill in the following fields: ${missingFields
          .map((field) => field.label)
          .join('\n ')}`
      );
      return;
    }

    setIsLoading(true);

    try {
      // Combine start and end date-time
      const startDateTime = `${eventStartDate}T${eventStartTime}`;
      const endDateTime = `${eventEndDate}T${eventEndTime}`;

      // Create the new event object
      const newEvent = {
        title: eventName,
        location: eventLocation,
        description: eventDescription,
        category: eventCategory,
        recurring: eventReoccurring,
        start: startDateTime,
        end: endDateTime,
      };

      // Save to Firestore as a sub-collection under `users/{uid}/events`
      const eventsCollectionRef = collection(
        db,
        `users/${globalUser.uid}/events`
      );
      await addDoc(eventsCollectionRef, newEvent);

      // Update the calendar's events state
      //setEvents((prevEvents) => [...prevEvents, newEvent]);

      // Provide user feedback
      toast.success('Event successfully added!');

      // Close modal and reset fields
      const modal = bootstrap.Modal.getInstance(
        document.getElementById('eventModal')
      );
      modal.hide();

      // Reset form fields
      setEventName('');
      setEventLocation('');
      setEventDescription('');
      setEventCategory('');
      setEventReoccurring('');
      setEventStartDate('');
      setEventStartTime('');
      setEventEndDate('');
      setEventEndTime('');
      setSelectedDate('');
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error('Failed to save the event. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className='row'>
        <div className='col-12'>
          <div className='row'>
            <div className='col-xl-3'>
              <div className='card card-h-100'>
                <div className='card-body'>
                  <button
                    className='btn btn-primary w-100'
                    id='btn-new-event'
                    data-bs-toggle='modal'
                    data-bs-target='#eventModal'
                  >
                    <i className='mdi mdi-plus'></i> New Entry
                  </button>
                  <hr />
                  <h6 className='text-muted'>Drag Event Below to Calendar</h6>
                  <div id='external-events'>
                    {myCalendarColors.map((item, index) => (
                      <div
                        key={index}
                        className={`external-event fc-event bg-${item.color}`}
                      >
                        <i className='mdi mdi-checkbox-blank-circle font-size-11 me-2'></i>
                        {item.value}
                      </div>
                    ))}
                  </div>
                  <hr />
                  <h6 className='text-muted'>Upcoming Appointments</h6>
                  <hr />
                  <div id='upcomingAppointments'></div>
                </div>
              </div>
            </div>

            <div className='col-xl-9'>
              <div className='card card-h-100'>
                <div className='card-body'>
                  <div id='smartCalendarLoader' className='position-relative'>
                    <div className='loader-overlay'>
                      <div className='loader'></div>
                    </div>
                    <div>
                      <FullCalendar
                        plugins={[
                          dayGridPlugin,
                          timeGridPlugin,
                          interactionPlugin,
                        ]}
                        initialView='dayGridMonth'
                        editable={true}
                        selectable={true}
                        events={events}
                        dateClick={handleDateClick}
                        eventClassNames={(eventInfo) => {
                          const category =
                            eventInfo.event.extendedProps.category;
                          const colorItem = myCalendarColors.find(
                            (item) => item.value === category
                          );
                          return colorItem
                            ? `bg-${colorItem.color}`
                            : 'bg-secondary';
                        }}
                        eventContent={(eventInfo) => (
                          <div className='custom-event'>
                            <strong>{eventInfo.event.title}</strong>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='modal fade' id='eventModal' tabIndex='-1'>
            <div className='modal-dialog modal-dialog-centered'>
              <div className='modal-content'>
                <div className='modal-header py-3 px-4 border-bottom-0'>
                  <h5 className='modal-title' id='modal-title'>
                    Event
                  </h5>

                  <button
                    type='button'
                    className='btn-close'
                    data-bs-dismiss='modal'
                    aria-hidden='true'
                  ></button>
                </div>
                <div className='modal-body p-4'>
                  <div className='col-12'>
                    <div className='form-floating mb-3'>
                      <input
                        type='text'
                        className='form-control'
                        value={eventName}
                        onChange={(e) => setEventName(e.target.value)}
                      />
                      <label className='form-label'>Event Name</label>
                    </div>
                  </div>

                  <div className='col-12'>
                    <div className='form-floating mb-3'>
                      <input
                        type='text'
                        className='form-control'
                        value={eventLocation}
                        onChange={(e) => setEventLocation(e.target.value)}
                      />
                      <label className='form-label'>Event Location</label>
                    </div>
                  </div>

                  <div className='col-12'>
                    <div className='form-floating mb-3'>
                      <input
                        type='text'
                        className='form-control'
                        value={eventDescription}
                        onChange={(e) => setEventDescription(e.target.value)}
                      />
                      <label className='form-label'>Event Description</label>
                    </div>
                  </div>

                  <div className='row'>
                    <div className='col-lg-6'>
                      <div className='form-floating mb-3'>
                        <select
                          className='form-select native-select'
                          onChange={(e) => setEventCategory(e.target.value)}
                        >
                          {myCalendarColors.map((subFolder) => (
                            <option key={subFolder.id} value={subFolder.value}>
                              {subFolder.value}
                            </option>
                          ))}
                        </select>
                        <label className='form-label'>Category</label>
                      </div>
                    </div>

                    <div className='col-lg-6'>
                      <div className='form-floating mb-3'>
                        <select
                          className='form-select'
                          onChange={(e) => setEventReoccurring(e.target.value)}
                        >
                          <option value=''>None</option>
                          <option value='Weekly'>Weekly</option>
                          <option value='Monthly'>Monthly</option>
                          <option value='Annually'>Annually</option>
                        </select>
                        <label className='form-label'>Reoccurring</label>
                      </div>
                    </div>
                  </div>

                  <div className='row'>
                    <div className='col-lg-6'>
                      <div className='form-floating mb-3'>
                        <input
                          type='date'
                          className='form-control'
                          value={eventStartDate}
                          onChange={(e) => setEventStartDate(e.target.value)}
                        />
                        <label className='form-label'>Start Date</label>
                      </div>
                    </div>

                    <div className='col-lg-6'>
                      <div className='form-floating mb-3'>
                        <input
                          type='time'
                          className='form-control'
                          value={eventStartTime}
                          onChange={(e) => setEventStartTime(e.target.value)}
                        />
                        <label className='form-label'>Start Time</label>
                      </div>
                    </div>
                  </div>

                  <div className='row'>
                    <div className='col-lg-6'>
                      <div className='form-floating mb-3'>
                        <input
                          type='date'
                          className='form-control'
                          value={eventEndDate}
                          onChange={(e) => setEventEndDate(e.target.value)}
                        />
                        <label className='form-label'>End Date</label>
                      </div>
                    </div>

                    <div className='col-lg-6'>
                      <div className='form-floating mb-3'>
                        <input
                          type='time'
                          className='form-control'
                          value={eventEndTime}
                          onChange={(e) => setEventEndTime(e.target.value)}
                        />
                        <label className='form-label'>End Time</label>
                      </div>
                    </div>
                  </div>

                  <div className='row mt-2'>
                    <div className='col-6'>
                      {selectedEvent && (
                        <button type='button' className='btn btn-danger'>
                          Delete
                        </button>
                      )}
                    </div>
                    <div className='col-6 text-end'>
                      <button
                        type='button'
                        className='btn btn-light me-1'
                        data-bs-dismiss='modal'
                      >
                        Close
                      </button>
                      <button
                        type='submit'
                        className='btn btn-success'
                        onClick={handleSaveEvent}
                      >
                        {isLoading ? (
                          <>
                            <i className='bx bx-loader bx-spin font-size-16 align-middle me-2'></i>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <i className='bx bx-event me-1'></i> Save
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* <!-- end modal--> */}
        </div>
      </div>
    </Layout>
  );
}

export default Calendar;
